import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import ContentSupervisorDashboard from './dashboards/ContentSupervisorDashboard';
import ContentGeneratorDashboard from './dashboards/ContentGeneratorDashboard';
import ContentReviewerDashboard from './dashboards/ContentReviewerDashboard';
import SuperAdminDashboard from './dashboards/SuperAdminDashboard';

/**
 * Role-based dashboard component that renders different dashboards based on user role
 */
const RoleBasedDashboard = () => {
  const { user, activeRole } = useAuth();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      setLoading(false);
    }
  }, [user]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Render dashboard based on active role (for Super Admin) or user role
  const roleToCheck = user?.isSuperAdmin ? activeRole : user?.role;

  switch (roleToCheck) {
    case 'CONTENT_SUPERVISOR':
      return <ContentSupervisorDashboard />;
    case 'CONTENT_GENERATOR':
      return <ContentGeneratorDashboard />;
    case 'CONTENT_REVIEWER':
      return <ContentReviewerDashboard />;
    case 'SUPER_ADMIN':
      return <SuperAdminDashboard />;
    case 'ANALYST':
      // Legacy analyst role - redirect to appropriate content role or show migration notice
      return (
        <div className="p-6">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <h2 className="text-lg font-semibold text-yellow-800 mb-2">
              Role Migration Required
            </h2>
            <p className="text-yellow-700 mb-4">
              The Analyst role has been enhanced into specialized content roles.
              Please contact your administrator to assign you to one of the following roles:
            </p>
            <ul className="list-disc list-inside text-yellow-700 space-y-1">
              <li><strong>Content Supervisor:</strong> Manage KITs, assign work, and oversee content workflow</li>
              <li><strong>Content Generator:</strong> Create questions and work with assigned KITs</li>
              <li><strong>Content Reviewer:</strong> Review and approve questions for publication</li>
            </ul>
          </div>
        </div>
      );
    default:
      return (
        <div className="p-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <h2 className="text-lg font-semibold text-red-800 mb-2">
              Access Denied
            </h2>
            <p className="text-red-700">
              You don't have permission to access this dashboard.
              Please contact your administrator for assistance.
            </p>
          </div>
        </div>
      );
  }
};

export default RoleBasedDashboard;
